import React, {
  useRef,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Select,
  MenuItem,
  Popover,
} from "@mui/material";
import { styled } from "@mui/system";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import momentPlugin from "@fullcalendar/moment";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import ReactDOM from "react-dom";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import MyTasksCalendarCard from "../MyTasksCalendarCard/MyTasksCalendarCard";
import { MyTasksCalendarStyles } from "./MyTasksCalendarStyles";
import { IAgentTask } from "../../models/agent/policiesListing";
import dropDownIcon from "../../assets/svgs/dropdown-carret.svg"; // Adjust the path as necessary
import moment from "moment";
import { dueDate } from "../../constants/Constants";
import { EditAddTask } from "../AddTaskForm/AddTaskActions";
import { useAppDispatch } from "../../store/hooks";

interface FullCalendarComponentProps {
  dayWiseEvents: IAgentTask[];
  setOpenTaskDialog: Dispatch<SetStateAction<boolean>>;
  setTaskModalData: Dispatch<SetStateAction<IAgentTask | null>>;
}

interface CalendarEvent {
  title: string;
  start: Date | string;
  allDay?: boolean;
  extendedProps: {
    tasks?: IAgentTask[];
    task?: IAgentTask;
  };
}

const StyledSelect = styled(Select)({
  width: "100px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.MuiSelect-select": {
    border: "none",
  },
});

const PopOverContainer = styled(Popover)({
  position: "absolute",
  "& .MuiPopover-paper": {
    top: "222px !important",
    left: "30px !important",
    "@media (max-width: 1023px)": {
      left: "13px !important",
      top: "241px !important",
    },
  },
});

const FullCalendarComponent: React.FC<FullCalendarComponentProps> = ({
  dayWiseEvents,
  setOpenTaskDialog,
  setTaskModalData,
}) => {
  console.log("dayWiseEvents:", dayWiseEvents);
  const dispatch = useAppDispatch();
  const calendarRef = useRef<any>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IAgentTask | null>(null);
  const [modalDate, setModalDate] = useState<Date | null>(null);
  const [modalMeetings, setModalMeetings] = useState<IAgentTask[]>([]);
  const isSmallScreen = useMediaQuery("(max-width:1023px)");
  const [currentView, setCurrentView] = useState<string>("dayGridMonth");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [, setSelectedDate] = useState<Date | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [taskList, setTaskList] = useState(false);
  const [, setIsRotated] = useState(false);

  // Add debugging for input data
  useEffect(() => {
    console.log(
      "Input dayWiseEvents:",
      dayWiseEvents.map((task) => ({
        id: task.id,
        dueDate: task.dueDate,
        dueTime: task.dueTime,
        taskName: task.taskName,
      }))
    );
  }, [dayWiseEvents]);

  const handleEventClick = (taskId: string) => {
    if (!taskId) return;
    const task = dayWiseEvents.find((task) => task.id === taskId);
    if (task) {
      setTaskModalData(task);
      setOpenTaskDialog(true);
      setModalData(task);
      setModalOpen(true);
    } else {
      setOpenTaskDialog(false);
      setTaskModalData(null);
      setModalOpen(false);
      setModalData(null);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setDetailsModalOpen(false);
    setModalData(null);
  };

  const handleDateSelect = (date: any) => {
    // Convert to YYYY-MM-DD string to avoid timezone issues
    const selectedDay = date.format
      ? date.format("YYYY-MM-DD")
      : date.$d?.toISOString().split("T")[0];

    setSelectedDate(selectedDay);

    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      const newViewType = calendarApi.view.type;
      // Use the date string directly, or create a Date at noon UTC
      calendarApi.changeView(newViewType, selectedDay);
      setCurrentView(newViewType);
      setPopoverOpen(false);
    }
  };

  const handleDatesSet = (dateInfo: any) => {
    console.log("Calendar view changed:", {
      viewType: dateInfo.view.type,
      start: dateInfo.start,
      end: dateInfo.end,
      currentDate: new Date().toISOString(),
    });
    const viewName = dateInfo.view.type;
    setCurrentView(viewName);
    if (viewName === "dayGridMonth") {
      const newDate = new Date(dateInfo.end);
      newDate.setMonth(newDate.getMonth() - 1);
      setCurrentDate(newDate);
    } else {
      setCurrentDate(new Date(dateInfo.start));
    }
  };

  const handleViewChange = (view: string) => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.changeView(view);
    }
    setCurrentView(view);
  };
  const addTimeLabel = () => {
    const timeGridHeader = document.querySelector(".fc-timegrid-axis-frame");
    if (timeGridHeader && !timeGridHeader.innerHTML.includes("Time")) {
      timeGridHeader.innerHTML = `
        <span style="font-weight: var(--font-weight-400); font-size: var(--font-size-16); color: var(--primary-black);font-family:'var(--font-noto-sans)'">Time</span>
      `;
    }
  };
  const handleTitleClick = () => {
    setPopoverOpen(true);
  };

  const handleClosePopover = () => {
    setPopoverOpen(false);
  };

  const handleTaskListclose = () => {
    setTaskList(false);
  };

  const groupTasksByDate = (dayWiseEvents: IAgentTask[]) => {
    const groupedTasks: { [key: string]: IAgentTask[] } = {};
    dayWiseEvents.forEach((task) => {
      if (!task.dueDate) {
        console.error("Task is missing dueDate:", task);
        return;
      }
      try {
        // Log the original date string
        console.log("Processing task date:", {
          taskId: task.id,
          originalDueDate: task.dueDate,
          originalDueTime: task.dueTime,
        });

        // Use the date string directly as key
        const dueDate = task.dueDate;
        if (!groupedTasks[dueDate]) {
          groupedTasks[dueDate] = [];
        }
        groupedTasks[dueDate].push(task);
      } catch (error) {
        console.error("Error processing task date:", error, task);
      }
    });

    // Log the grouped tasks
    console.log(
      "Grouped tasks:",
      Object.entries(groupedTasks).map(([date, tasks]) => ({
        date,
        taskCount: tasks.length,
        tasks: tasks.map((t) => ({
          id: t.id,
          dueDate: t.dueDate,
          dueTime: t.dueTime,
        })),
      }))
    );

    return groupedTasks;
  };

  const groupedTasks = groupTasksByDate(dayWiseEvents);
  console.log("groupedTasks:", groupedTasks);

  const handleDateClick = (date: Date) => {
    setModalDate(date);
    const dateString = date.toISOString().split("T")[0];
    setModalMeetings(groupedTasks[dateString] || []);
    setModalOpen(true);
  };

  const standardMeetingCount = 3;

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    const injectCustomTitle = () => {
      const toolbarElement = document.querySelector(".fc-toolbar-chunk");

      if (toolbarElement) {
        ReactDOM.render(
          <Box
            sx={MyTasksCalendarStyles.monthTitle}
            onClick={() => setIsRotated((prev) => !prev)}
          >
            <Typography sx={MyTasksCalendarStyles.toolbarHeader}>
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </Typography>
            <Box
              component="img"
              src={dropDownIcon}
              alt="Custom Icon"
              sx={{
                ...MyTasksCalendarStyles.dropDownIcon,
                transform: popoverOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Box>,
          toolbarElement
        );
      }
    };

    const addTitleClickListener = () => {
      const titleElement = document.querySelector(".fc-toolbar-chunk");
      if (titleElement) {
        (titleElement as HTMLElement).style.cursor = "pointer";
        titleElement.addEventListener("click", handleTitleClick);
      }
    };

    const removeTitleClickListener = () => {
      const titleElement = document.querySelector(".fc-toolbar-chunk");
      if (titleElement) {
        titleElement.removeEventListener("click", handleTitleClick);
      }
    };

    if (calendarApi) {
      injectCustomTitle();
      addTitleClickListener();
    }

    return () => {
      removeTitleClickListener();
    };
  }, [calendarRef, currentView, currentDate, popoverOpen]);

  const getSxForView = () => {
    return currentView === "timeGridWeek" || currentView === "dayGridMonth"
      ? {
          "& .fc-theme-standard th": {
            width: "231px",
          },

          "& .fc-scroller": {
            position: "relative",
          },
          ...(currentView === "dayGridMonth" && {
            "& .fc table": {
              borderCollapse: "separate !important",
            },
          }),
        }
      : {
          "& .fc-theme-standard th": {
            width: "unset",
          },
        };
  };

  const getCurrentTime = () => {
    const date = new Date();
    return `${date.getHours()}:${date.getMinutes()}:00`;
  };

  const handleScrollToCurrentTime = () => {
    setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi && calendarRef.current) {
        const calendarEl = calendarRef.current.el;

        if (calendarEl) {
          const scrollContainer = calendarEl.querySelector(".fc-scroller");
          const currentTimeElement = calendarEl.querySelector(
            ".fc-now-indicator-line"
          );
          if (scrollContainer && currentTimeElement) {
            const scrollOffset = 50;
            const currentTimePosition = currentTimeElement.offsetTop;
            scrollContainer.scrollTop = currentTimePosition - scrollOffset;
          }
        }
      }
    }, 0);
  };

  // Update calendar options
  const calendarOptions = {
    timeZone: "UTC", // Force UTC to prevent date shifting
    displayEventTime: false,
    displayEventEnd: false,
    eventTimeFormat: {
      hour: "2-digit" as const,
      minute: "2-digit" as const,
      hour12: true,
      meridiem: "short" as const,
    },
  };

  let eventsProp: CalendarEvent[];
  if (currentView?.toLowerCase() === "daygridmonth") {
    eventsProp = Object.keys(groupedTasks)?.map((date) => {
      const tasks = groupedTasks[date];

      // Create a UTC date at noon to ensure consistent date handling
      const [year, month, day] = date.split("-").map(Number);
      const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

      console.log("Creating month view event:", {
        originalDate: date,
        utcDate: utcDate.toISOString(),
        taskCount: tasks.length,
      });

      return {
        title: `${tasks?.length} tasks`,
        start: utcDate.toISOString(), // Use ISO string with UTC time
        allDay: true,
        extendedProps: { tasks },
      };
    });
  } else {
    eventsProp = Object.keys(groupedTasks)?.flatMap((date) =>
      groupedTasks[date]?.map((task) => {
        const [year, month, day] = date.split("-").map(Number);
        const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

        if (task.dueTime) {
          const [hours, minutes] = task.dueTime.split(":").map(Number);
          utcDate.setUTCHours(hours, minutes, 0, 0);
        }

        return {
          title: task.taskName,
          start: utcDate.toISOString(),
          allDay: !task.dueTime,
          extendedProps: { task },
        };
      })
    );
  }
  console.log("eventsProp:", eventsProp);

  const renderTime = (time: string | Date) => {
    if (!time) {
      console.error("Invalid time:", time);
      return "Invalid Time";
    }

    let hours: number;
    let minutes: number;

    if (time instanceof Date) {
      // Handle Date object (from week/day views)
      hours = time.getUTCHours();
      minutes = time.getUTCMinutes();
    } else if (typeof time === "string") {
      // Handle string input
      if (time.includes("T")) {
        // ISO string format
        const dateObj = new Date(time);
        hours = dateObj.getUTCHours();
        minutes = dateObj.getUTCMinutes();
      } else {
        // HH:mm:ss format
        const [timeHours, timeMinutes] = time.split(":").map(Number);
        hours = timeHours;
        minutes = timeMinutes;
      }
    } else {
      console.error("Unexpected time format:", time);
      return "Invalid Time";
    }

    return formatTime(hours, minutes);
  };

  // Helper function to format time consistently
  const formatTime = (hours: number, minutes: number) => {
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  return (
    <Box
      sx={MyTasksCalendarStyles.calendarContainer}
      id="calendar-container"
      data-test-id="calendar-container"
      aria-label="Calendar container"
    >
      {isSmallScreen && (
        <Box
          sx={MyTasksCalendarStyles.mobileHeaderContainer}
          id="mobile-header"
          data-test-id="mobile-header"
          aria-label="Mobile header"
        >
          <Box
            sx={MyTasksCalendarStyles.mobileDropdown}
            onClick={handleTitleClick}
            id="mobile-dropdown"
            data-test-id="mobile-dropdown"
            aria-label="Mobile dropdown"
          >
            <Typography>
              {currentDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}{" "}
            </Typography>
            <Box
              component="img"
              src={dropDownIcon}
              alt="Custom Icon"
              sx={{
                ...MyTasksCalendarStyles.dropDownIcon,
                transform: popoverOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
              id="dropdown-icon"
              data-test-id="dropdown-icon"
              aria-label="Dropdown icon"
            />
          </Box>
          <Box
            sx={MyTasksCalendarStyles.selectContainer}
            id="view-selector"
            data-test-id="view-selector"
            aria-label="View selector"
          >
            <StyledSelect
              value={currentView}
              onChange={(e) => handleViewChange(e.target.value as string)}
              id={`${currentView}`}
              data-test-id={`test-${currentView}`}
              aria-label={`${currentView}`}
            >
              <MenuItem value="dayGridMonth">Month</MenuItem>
              <MenuItem value="timeGridWeek">Week</MenuItem>
              <MenuItem value="timeGridDay">Day</MenuItem>
            </StyledSelect>
          </Box>
        </Box>
      )}
      <PopOverContainer
        open={popoverOpen}
        anchorEl={calendarRef.current}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        id="calendar-popover"
        data-test-id="calendar-popover"
        aria-label="Calendar popover"
      >
        <Box sx={{ padding: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              id="date-calendar"
              data-test-id="date-calendar"
              aria-label="Date calendar"
            >
              <DateCalendar onChange={handleDateSelect} />
            </Box>
          </LocalizationProvider>
        </Box>
      </PopOverContainer>
      <Box sx={isSmallScreen ? {} : getSxForView()}>
        <Box
          id="full-calendar"
          data-test-id="full-calendar"
          aria-label="Full calendar"
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              momentPlugin,
            ]}
            {...calendarOptions}
            initialView="dayGridMonth"
            nowIndicator={true}
            scrollTime={getCurrentTime()}
            datesSet={handleDatesSet}
            events={eventsProp}
            eventOverlap={false}
            slotEventOverlap={false}
            dateClick={(info) => handleDateClick(new Date(info.dateStr))}
            headerToolbar={
              !isSmallScreen && {
                center: "today prev,next",
                left: "",
                right: isSmallScreen
                  ? undefined
                  : "timeGridDay,timeGridWeek,dayGridMonth",
              }
            }
            views={{
              timeGridWeek: {
                scrollTime: getCurrentTime(),
                dayHeaderFormat: isSmallScreen
                  ? { weekday: "narrow", day: "2-digit" }
                  : { weekday: "long", day: "2-digit" },
              },
              timeGridDay: {
                scrollTime: getCurrentTime(),
                dayHeaderFormat: isSmallScreen
                  ? { weekday: "short", day: "2-digit" }
                  : { weekday: "long", day: "2-digit" },
              },
            }}
            dayHeaderFormat={
              isSmallScreen ? { weekday: "narrow" } : { weekday: "long" }
            }
            viewDidMount={(info) => {
              if (
                info.view.type === "timeGridDay" ||
                info.view.type === "timeGridWeek"
              ) {
                addTimeLabel();
                if (typeof handleScrollToCurrentTime === "function") {
                  handleScrollToCurrentTime();
                }
              }
            }}
            eventContent={(info: any) => {
              console.log("info:", info);

              if (!groupedTasks || Object.keys(groupedTasks)?.length === 0)
                return null;

              const handleFullDetails = (date: Date) => {
                setModalMeetings(
                  groupedTasks[date.toISOString().split("T")[0]] || []
                );
                setModalDate(date);
                setTaskList(true);
              };

              const topMeetings = (
                groupedTasks[
                  new Date(info.event.start).toISOString().replace(/T.*$/, "")
                ] || []
              ).slice(0, standardMeetingCount);
              const remainingCount =
                (groupedTasks[
                  new Date(info.event.start).toISOString().replace(/T.*$/, "")
                ]?.length || 0) - standardMeetingCount;
              return currentView?.toLowerCase() === "daygridmonth" ? (
                <Box sx={MyTasksCalendarStyles.totalContainer}>
                  <Box sx={MyTasksCalendarStyles.totalMeetingsMonthContainer}>
                    {topMeetings?.map((task: IAgentTask) => {
                      return (
                        <Box
                          key={task.id}
                          onClick={() => handleEventClick(task.id)}
                          sx={
                            task.priority?.toLowerCase() === "low"
                              ? MyTasksCalendarStyles.lowPriorityContainer
                              : task.priority?.toLowerCase() === "medium"
                              ? MyTasksCalendarStyles.mediumPriorityContainer
                              : MyTasksCalendarStyles.highPriorityContainer
                          }
                        >
                          <Box sx={MyTasksCalendarStyles.innerTextContainer}>
                            <Box
                              sx={
                                task.priority?.toLowerCase() === "low"
                                  ? MyTasksCalendarStyles.lowDot
                                  : task.priority?.toLowerCase() === "medium"
                                  ? MyTasksCalendarStyles.mediumDot
                                  : MyTasksCalendarStyles.highDot
                              }
                            ></Box>
                            <Typography sx={MyTasksCalendarStyles.withWhom}>
                              {task.customerName}{" "}
                            </Typography>
                          </Box>
                          <Typography sx={MyTasksCalendarStyles.formattedTime}>
                            {task.dueTime
                              ? renderTime(task.dueTime)
                              : formatTime(0, 0)}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {remainingCount > 0 && (
                    <Typography
                      id="Total Meetings"
                      data-test-id="test-total-meeting"
                      aria-label="Total Meeting count"
                      onClick={() =>
                        handleFullDetails(new Date(info.event.start))
                      }
                      sx={MyTasksCalendarStyles.remaining}
                    >
                      +{remainingCount} More
                    </Typography>
                  )}
                </Box>
              ) : currentView?.toLowerCase() === "timegridweek" ? (
                info?.event?.extendedProps?.task && (
                  <Box
                    onClick={() =>
                      handleEventClick(info?.event?.extendedProps?.task?.id)
                    }
                    key={info.event.extendedProps.task.id}
                    sx={
                      info?.event?.extendedProps?.task?.priority?.toLowerCase() ===
                      "low"
                        ? MyTasksCalendarStyles.lowPriorityWeekContainer
                        : info?.event?.extendedProps?.task?.priority?.toLowerCase() ===
                          "medium"
                        ? MyTasksCalendarStyles.mediumPriorityWeekContainer
                        : MyTasksCalendarStyles.highPriorityWeekContainer
                    }
                  >
                    <Box sx={MyTasksCalendarStyles.innerWeekTextContainer}>
                      <Typography
                        sx={{
                          ...MyTasksCalendarStyles.withWhom,
                          ...(currentView?.toLowerCase() === "timegridweek" && {
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }),
                        }}
                      >
                        {
                          info?.event?.extendedProps?.task?.customerName?.split(
                            " "
                          )[0]
                        }
                      </Typography>
                      {/* <Box sx={MyTasksCalendarStyles.timeContainer}> */}
                      {/* <Typography sx={MyTasksCalendarStyles.formattedTime}>
                        {renderTime(info.event.start)}
                      </Typography> */}
                      {/* </Box> */}
                    </Box>
                    {/* <Typography sx={MyTasksCalendarStyles.meetingTitle}>
                      {info?.event?.extendedProps?.task?.taskName}
                    </Typography> */}
                  </Box>
                )
              ) : (
                <Box
                  onClick={() =>
                    handleEventClick(info?.event?.extendedProps?.task?.id)
                  }
                  key={info?.event?.extendedProps?.task?.id}
                  sx={
                    info?.event?.extendedProps?.task?.priority?.toLowerCase() ===
                    "low"
                      ? MyTasksCalendarStyles.lowPriorityWeekContainer
                      : info?.event?.extendedProps?.task?.priority?.toLowerCase() ===
                        "medium"
                      ? MyTasksCalendarStyles.mediumPriorityWeekContainer
                      : MyTasksCalendarStyles.highPriorityWeekContainer
                  }
                >
                  {isSmallScreen ? (
                    <Typography sx={MyTasksCalendarStyles.meetingTitle}>
                      {info?.event?.extendedProps?.task?.taskName}
                    </Typography>
                  ) : (
                    <>
                      <Box sx={MyTasksCalendarStyles.innerWeekTextContainer}>
                        <Typography sx={MyTasksCalendarStyles.withWhom}>
                          {info?.event?.extendedProps?.task?.customerName}
                        </Typography>
                        {/*  <Box sx={MyTasksCalendarStyles.timeContainer}>
                          <Typography sx={MyTasksCalendarStyles.formattedTime}>
                            {renderTime(info.event.start)}
                          </Typography>
                        </Box> */}
                      </Box>
                      {/* <Typography sx={MyTasksCalendarStyles.formattedTime}>
                        {info?.event?.extendedProps?.task?.taskName}
                      </Typography> */}
                    </>
                  )}
                </Box>
              );
            }}
            slotLabelFormat={{
              hour: "2-digit",
              hour12: true,
            }}
            eventDidMount={(info) => {
              console.log("Event rendered:", {
                event: info.event,
                start: info.event.start,
                end: info.event.end,
                allDay: info.event.allDay,
                view: info.view.type,
              });
            }}
          />
        </Box>
      </Box>
      <Box
        id="task-modal"
        data-test-id="task-modal"
        aria-label="Task details modal"
      >
        <ModalComponent
          heading={
            modalDate
              ? new Date(modalDate).toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
              : "Task Details"
          }
          open={taskList}
          handleOpen={handleTaskListclose}
          additionalClassContainer={
            MyTasksCalendarStyles.modalMeetingsContainer
          }
        >
          <Box
            sx={MyTasksCalendarStyles.totalMeetingsContainer}
            id="meetings-container"
            data-test-id="meetings-container"
            aria-label="Meetings container"
          >
            {modalMeetings?.map((meeting) => (
              <Box
                key={meeting.id}
                sx={
                  meeting.priority?.toLowerCase() === "low"
                    ? MyTasksCalendarStyles.lowPriorityModalContainer
                    : meeting.priority?.toLowerCase() === "medium"
                    ? MyTasksCalendarStyles.mediumPriorityModalContainer
                    : MyTasksCalendarStyles.highPriorityModalContainer
                }
                onClick={() => handleEventClick(meeting.id)}
                id={`meeting-${meeting.id}`}
                data-test-id={`meeting-${meeting.id}`}
                aria-label={`Meeting with ${meeting.customerName}`}
              >
                <Box sx={MyTasksCalendarStyles.innerTextContainer}>
                  <Box
                    sx={
                      meeting.priority?.toLowerCase() === "low"
                        ? MyTasksCalendarStyles.lowModalDot
                        : meeting.priority?.toLowerCase() === "medium"
                        ? MyTasksCalendarStyles.mediumModalDot
                        : MyTasksCalendarStyles.highModalDot
                    }
                    id={`priority-dot-${meeting.id}`}
                    data-test-id={`priority-dot-${meeting.id}`}
                    aria-label={`${meeting.priority} priority indicator`}
                  ></Box>
                  <Typography
                    sx={MyTasksCalendarStyles.withWhom}
                    id={`customer-name-${meeting.id}`}
                    data-test-id={`customer-name-${meeting.id}`}
                    aria-label={`Customer name: ${meeting.customerName}`}
                  >
                    {meeting.customerName}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </ModalComponent>
      </Box>
    </Box>
  );
};

export default FullCalendarComponent;
